import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { Col, Dropdown, FormLabel, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import PageTitle from "../../../layouts/PageTitle";
import GridCustomBase from "../../../components/GridCustom/GridCustomBase";
import FilterAccordion from "../../../components/Common/Filter/FilterAccordion";
import EmployeeSelect from "../../../components/Custom/Main/Employee/EmployeeSelect";
import InputDate from "../../../components/Forms/Element/InputDate";
import CustomSelect from "../../../components/Forms/Element/CustomSelect";
import ListMetricCard, { ListMetricCards } from "../../../components/Common/ListParts/ListMetricCard";
import StatusPill from "../../../components/Common/ListParts/StatusPill";
import ModalPageEvent from "../../../components/Common/Modal/ModalPageEvent";
import DetailIcon from "../../../components/Icon/DetailIcon";
import CancelIcon from "../../../components/Icon/CancelIcon";
import EmployeePaymentBreakdown from "../../../components/Custom/CashFlow/EmployeePayment/EmployeePaymentBreakdown";
import EmployeePaymentCancelModal from "../../../components/Custom/CashFlow/EmployeePayment/EmployeePaymentCancelModal";
import { ApiError } from "../../../components/Common/ApiError";
import Common from "../../../../constants/Common";
import Api from "../../../../constants/Api";
import axiosInstance from "../../../../services/AxiosInstance";
import { SearchOperation } from "../../../../utils/ApiUtils";
import { convertDoubleToCurrencyPTBR } from "../../../../utils/ConverterUtils";
import { formatDate, formatDateTime, formatDateToYYYYMMDD } from "../../../../utils/DateUtils";
import "./EmployeePaymentList.css";

const startOfMonth = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
};

// Com uma ou duas linhas, o menu ultrapassa a área rolável da tabela. Renderizá-lo no body
// mantém o mesmo Dropdown do financeiro sem esconder a opção de cancelamento.
const RowActionsMenu = forwardRef(({ children, style, className, "aria-labelledby": labeledBy }, ref) =>
    ReactDOM.createPortal(
        <div ref={ref} style={style} className={className} aria-labelledby={labeledBy}>
            {children}
        </div>, document.body
    )
);

/** Histórico pesquisado pela data do pagamento, sem exigir que o gestor lembre o período da comissão. */
const EmployeePaymentList = () => {
    const { t } = useTranslation();
    const [startDate, setStartDate] = useState(startOfMonth);
    const [endDate, setEndDate] = useState(() => new Date());
    const [employee, setEmployee] = useState(null);
    const [status, setStatus] = useState(null);
    const [details, setDetails] = useState(null);
    const [cancelTarget, setCancelTarget] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    const [showCancel, setShowCancel] = useState(false);
    const [totals, setTotals] = useState({ paid: 0, paidCount: 0, canceledCount: 0 });
    const [loadingTotals, setLoadingTotals] = useState(false);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(Common.DEFAULT_ROWS_PER_PAGE);
    const [pageable, setPageable] = useState(null);
    const [rows, setRows] = useState([]);
    const [loadingRows, setLoadingRows] = useState(false);

    const statusOptions = useMemo(() => [
        { value: 'PAID', label: t('cash_flow.employee_payment_status_paid') },
        { value: 'CANCELED', label: t('cash_flow.employee_payment_status_canceled') }
    ], [t]);

    const buildFilters = useCallback((start, end, selectedEmployee, selectedStatus) => {
        const next = [];
        if (start) next.push({ field: 'paymentDate', value: formatDateToYYYYMMDD(start), operation: SearchOperation.GREATER_THAN_EQUAL });
        if (end) next.push({ field: 'paymentDate', value: formatDateToYYYYMMDD(end), operation: SearchOperation.LESS_THAN_EQUAL });
        if (selectedEmployee) next.push({ field: 'employee.id', value: selectedEmployee.value, operation: SearchOperation.EQUAL });
        if (selectedStatus) next.push({ field: 'paymentStatus', value: selectedStatus.value, operation: SearchOperation.EQUAL });
        return next;
    }, []);
    const [filters, setFilters] = useState(() => buildFilters(startOfMonth(), new Date(), null, null));

    // Os cards usam todos os registros do filtro; a grade traz somente a página visível.
    useEffect(() => {
        setLoadingTotals(true);
        axiosInstance.post(`${Api.cash_flow.employee_payment}/findByFilters`, { filters })
            .then(({ data }) => {
                const rows = data || [];
                const paid = rows.filter(row => row.paymentStatus === 'PAID');
                setTotals({
                    paid: paid.reduce((sum, row) => sum + Number(row.netValue || 0), 0),
                    paidCount: paid.length,
                    canceledCount: rows.length - paid.length
                });
            })
            .catch(error => ApiError(error))
            .finally(() => setLoadingTotals(false));
    }, [filters]);

    // Mesmo componente de tabela e paginação usado por Contas a Pagar/Receber.
    useEffect(() => {
        setLoadingRows(true);
        axiosInstance.post(`${Api.cash_flow.employee_payment}/pageable`, {
            filters, page, size, orders: [{ field: 'id', direction: 'DESC' }]
        })
            .then(({ data }) => {
                setPageable(data);
                setRows(data?.content || []);
            })
            .catch(error => ApiError(error))
            .finally(() => setLoadingRows(false));
    }, [filters, page, size]);

    const openDetails = useCallback((payment) => {
        setDetails(payment);
        setShowDetails(true);
    }, []);
    const openCancel = useCallback((payment) => {
        setCancelTarget(payment);
        setShowCancel(true);
    }, []);
    const reload = () => {
        setPage(0);
        setFilters(current => [...current]);
    };
    const clear = () => {
        // Limpar abre o histórico inteiro para localizar acertos antigos sem saber o período.
        setStartDate(null);
        setEndDate(null);
        setEmployee(null);
        setStatus(null);
        setPage(0);
        setFilters([]);
    };

    const meta = (label, value) => <div><span>{label}</span><strong>{value || '-'}</strong></div>;

    return <>
        <PageTitle activeMenu={t('cash_flow.employee_payments')} motherMenu={t('cash_flow.label')} />

        <FilterAccordion title="filters" onSearch={() => {
            setPage(0);
            setFilters(buildFilters(startDate, endDate, employee, status));
        }}
            onClear={clear} centerButtons={false}>
            <Row className="g-2 align-items-end">
                <Col lg={3} md={6}><FormLabel>{t('start_date')}</FormLabel>
                    <InputDate selected={startDate} onChange={setStartDate} /></Col>
                <Col lg={3} md={6}><FormLabel>{t('end_date')}</FormLabel>
                    <InputDate selected={endDate} onChange={setEndDate} /></Col>
                <Col lg={3} md={6}><FormLabel>{t('employee')}</FormLabel>
                    <EmployeeSelect filterProvidesService={false} value={employee} onChange={setEmployee} /></Col>
                <Col lg={3} md={6}><FormLabel>{t('status')}</FormLabel>
                    <CustomSelect options={statusOptions} value={status} onChange={setStatus}
                        isClearable placeholder={t('select')} /></Col>
            </Row>
        </FilterAccordion>

        <ListMetricCards>
            <ListMetricCard accent="success" label={t('cash_flow.employee_payments_total')}
                value={convertDoubleToCurrencyPTBR(totals.paid)} icon="fas fa-money-bill-transfer" loading={loadingTotals} />
            <ListMetricCard accent="primary" label={t('cash_flow.employee_payments_count')}
                value={totals.paidCount} icon="fas fa-receipt" loading={loadingTotals} />
            <ListMetricCard accent="info" label={t('cash_flow.employee_payments_canceled')}
                value={totals.canceledCount} icon="fas fa-ban" loading={loadingTotals} />
        </ListMetricCards>

        <GridCustomBase pageable={pageable} setPage={setPage} setSize={setSize}
            showLoading={loadingRows} height={Math.min(600, Math.max(500, rows.length * 62 + 54))}>
            <table className="table table-list i-table style-1 mb-0 border-0 employee-payment-list__table"
                style={{ minWidth: 1120 }}>
                <thead><tr>
                    <th>{t('employee')}</th>
                    <th>{t('cash_flow.employee_payment_date')}</th>
                    <th>{t('cash_flow.employee_payment_period')}</th>
                    <th>{t('cash_flow.employee_payment_net')}</th>
                    <th>{t('status')}</th>
                    <th>{t('branch')}</th>
                    <th className="bg-none employee-payment-list__actions-cell" style={{ width: 60 }} aria-label={t('actions')}></th>
                </tr></thead>
                <tbody>
                    {rows.map(payment => <tr key={payment.id}>
                        <td>{payment.employee?.name || '-'}</td>
                        <td>{payment.paymentDate ? formatDate(payment.paymentDate) : '-'}</td>
                        <td>{t('cash_flow.employee_payment_period_range', {
                            start: formatDate(payment.periodStart), end: formatDate(payment.periodEnd)
                        })}</td>
                        <td><strong>{convertDoubleToCurrencyPTBR(payment.netValue || 0)}</strong></td>
                        <td><StatusPill tone={payment.paymentStatus?.toLowerCase()}>
                            {t(`cash_flow.employee_payment_status_${payment.paymentStatus?.toLowerCase()}`)}
                        </StatusPill></td>
                        <td>{payment.branch?.name || '-'}</td>
                        <td className="employee-payment-list__actions-cell">
                            <Dropdown className="d-inline-flex">
                                <Dropdown.Toggle variant="link" className="employee-payment-list__row-action i-false"
                                    aria-label={t('actions')}>
                                    <i className="fas fa-ellipsis-v" aria-hidden="true" />
                                </Dropdown.Toggle>
                                <Dropdown.Menu as={RowActionsMenu}>
                                    <Dropdown.Item onClick={() => openDetails(payment)}>
                                        <DetailIcon className="me-2" /> {t('cash_flow.employee_payment_details')}
                                    </Dropdown.Item>
                                    {payment.paymentStatus === 'PAID' && <Dropdown.Item onClick={() => openCancel(payment)}>
                                        <span className="me-2"><CancelIcon /></span> {t('cash_flow.employee_payment_cancel')}
                                    </Dropdown.Item>}
                                </Dropdown.Menu>
                            </Dropdown>
                        </td>
                    </tr>)}
                    {!loadingRows && rows.length === 0 && <tr><td colSpan={7} className="text-center py-4">
                        {t('message.no_records_found')}
                    </td></tr>}
                </tbody>
            </table>
        </GridCustomBase>

        <ModalPageEvent show={showDetails} setShow={setShowDetails} size="lg"
            title={t('cash_flow.employee_payment_details')}
            child={details && <>
                <div className="employee-payment-list__meta">
                    {meta(t('employee'), details.employee?.name)}
                    {meta(t('branch'), details.branch?.name)}
                    {meta(t('cash_flow.employee_payment_date'), details.paymentDate && formatDate(details.paymentDate))}
                    {meta(t('cash_flow.employee_payment_period'), t('cash_flow.employee_payment_period_range', {
                        start: formatDate(details.periodStart), end: formatDate(details.periodEnd)
                    }))}
                    {meta(t('payment_method'), details.paymentMethod && t(details.paymentMethod.toLowerCase()))}
                    {meta(t('cash_flow.employee_payment_category'), details.category?.name)}
                    {meta(t('cash_flow.employee_payment_payable'), details.payableId)}
                    {meta(t('cash_flow.employee_payment_registered_by'), details.registeredBy?.name)}
                    {details.paymentStatus === 'CANCELED' && <>
                        {meta(t('cash_flow.employee_payment_canceled_at'), details.canceledAt && formatDateTime(details.canceledAt))}
                        {meta(t('cash_flow.employee_payment_canceled_by'), details.canceledBy?.name)}
                        {meta(t('reason'), details.cancelReason)}
                    </>}
                </div>
                <EmployeePaymentBreakdown values={details} />
            </>}
            btnText={details?.paymentStatus === 'PAID' ? t('cash_flow.employee_payment_cancel') : undefined}
            btnOnClick={details?.paymentStatus === 'PAID' ? () => {
                setShowDetails(false);
                openCancel(details);
            } : undefined}
        />
        <EmployeePaymentCancelModal show={showCancel} setShow={setShowCancel}
            payment={cancelTarget} onCanceled={reload} />
    </>;
};

export default EmployeePaymentList;
